<template>
  <div class="environmental-impact container">
    <div class="current-site">
      <div class="title">{{ $t('CurrentSiteName') }}</div>
      <div class="value">{{ screenData.environmentalImpact.toFixed(1) }} kg</div>
    </div>
    <svg class="chart"></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { colors } from '@/pure/TargetSites';

export default {
  props: ['screenData', 'index'],

  data() {
    return {
      sites: 0,
      width: 1560,
      height: 594,
      barWidth: 96,
      svg: null,
      bars: null,
      yScale: null,
    };
  },

  computed: {
    impact() {
      return this.screenData.sites.map(site => {
        return {
          name: site.name,
          impact: site.details.environmentalImpact,
          impactPercent: this.screenData.environmentalImpact > 0 ? site.details.environmentalImpact / this.screenData.environmentalImpact - 1 : 0,
          color: colors[site.order + 1],
        };
      });
    },

    padding() {
      return this.impact && this.impact.length > 0 ? (this.width - this.impact.length * this.barWidth) / this.impact.length : 0;
    },
  },

  watch: {
    index() {
      this.updateGraph(true);
    },

    screenData() {
      if (this.sites === this.impact.length) {
        this.updateGraph();
      } else {
        this.svg.selectAll('.bar').remove();
        this.svg.selectAll('line').remove();
        this.svg.selectAll('text').remove();
        this.svg = null;
        this.bars = null;
        this.yScale = null;

        setTimeout(this.initGraph, 100);
      }

      this.sites = this.impact.length;
    },
  },

  mounted() {
    this.initGraph();
    this.sites = this.impact.length;
  },

  methods: {
    initGraph() {
      const barWidth = this.barWidth;
      const padding = this.padding;
      const margin = { left: padding / 2, right: padding / 2, top: 24, bottom: 24 };
      const chartHeight = this.height;
      const chartWidth = this.width;

      const max = d3.max(this.impact, d => Math.abs(d.impactPercent));
      this.yScale = d3
        .scaleLinear()
        .domain([0, Math.max(max * 2, 1)])
        .range([0, chartHeight]);

      this.svg = d3.select(this.$el.querySelector('.chart')).attr('height', chartHeight).attr('width', chartWidth).attr('transform', `translate(0, 100)`);

      this.bars = this.svg.selectAll('.bar').data(this.impact);
      this.bars
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', (_, i) => {
          return margin.left + i * (barWidth + padding);
        })
        .attr('y', () => {
          return chartHeight;
        })
        .attr('height', 0)
        .attr('width', barWidth)
        .attr('fill', d => d.color)
        .style('opacity', (d, i) => (this.index === -1 || this.index === i ? 1 : 0.2))
        .attr('transform', `translate(0, ${this.height / -2})`)
        .merge(this.bars)
        .transition()
        .duration(700)
        .attr('y', d => {
          return chartHeight - Math.max(0, this.yScale(d.impactPercent));
        })
        .attr('height', d => Math.abs(this.yScale(d.impactPercent)));

      this.bars
        .exit()
        .transition()
        .duration(700)
        .attr('y', function (d) {
          return chartHeight - Math.max(0, this.yScale(d.impactPercent));
        })
        .attr('height', d => Math.abs(this.yScale(d.impactPercent)))
        .remove();

      this.svg
        .selectAll('line')
        .data([0])
        .enter()
        .append('svg:line')
        .attr('x1', 0)
        .attr('x2', this.width)
        .attr('y1', this.height / 2)
        .attr('y2', this.height / 2)
        .attr('stroke', '#fff');

      const labels = this.svg.selectAll('text').data(this.impact);
      const newLabels = labels
        .enter()
        .append('text')
        .attr('x', (d, i) => {
          return margin.left + i * (barWidth + padding) + barWidth / 2;
        })
        .attr('y', d => {
          return chartHeight - this.yScale(d.impactPercent) - 64 * Math.sign(d.impactPercent);
        })
        .attr('font-family', 'sans-serif')
        .style('opacity', (d, i) => (this.index === -1 || this.index === i ? 1 : 0.2))
        .style('text-anchor', 'middle')
        .attr('transform', `translate(0, ${this.height / -2})`);

      newLabels
        .append('tspan')
        .attr('class', 'site-value')
        .text(d => `${d.impactPercent >= 0 ? '+' : ''}${Math.round(d.impactPercent * 100)} %`)
        .attr('x', (d, i) => margin.left + i * (barWidth + padding) + barWidth / 2)
        .attr('font-size', '64px')
        .attr('font-weight', '200')
        .style('text-anchor', 'middle');

      newLabels
        .append('tspan')
        .attr('class', 'site-name')
        .text(d => (d.name && d.name.length > 12 ? `${d.name.slice(0, 9)}...` : d.name))
        .attr('x', (d, i) => margin.left + i * (barWidth + padding) + barWidth / 2)
        .attr('dy', '1.6em')
        .attr('font-size', '24px')
        .attr('font-weight', 'bold')
        .style('text-anchor', 'middle');

      const values = this.svg.selectAll('.site-value').data(this.impact);
      values
        .enter()
        .merge(values)
        .transition()
        .duration(700)
        .tween('text', function (d) {
          const text = d3.select(this).text();
          const current = isNaN(text) ? Number(text.split('%')[0]) / 100 : text;
          var i = d3.interpolate(Number(current), d.impactPercent);
          return t => {
            d3.select(this).text(d3.format('+.0%')(i(t)));
          };
        });
    },

    updateGraph(indexMode) {
      const chartHeight = this.height;

      const max = d3.max(this.impact, d => Math.abs(d.impactPercent));

      this.yScale = d3
        .scaleLinear()
        .domain([0, Math.max(max * 2, 1)])
        .range([0, chartHeight]);

      const bars = this.svg.selectAll('.bar').data(this.impact);
      bars
        .enter()
        .style('opacity', 0)
        .merge(bars)
        .transition()
        .duration(700)
        .attr('y', d => {
          return chartHeight - Math.max(0, this.yScale(d.impactPercent));
        })
        .attr('height', d => Math.abs(this.yScale(d.impactPercent)))
        .style('opacity', (d, i) => (this.index === -1 || this.index === i ? 1 : 0.2));

      const labels = this.svg.selectAll('text').data(this.impact);
      labels
        .enter()
        .style('opacity', 0)
        .merge(labels)
        .transition()
        .duration(700)
        .style('opacity', (d, i) => (this.index === -1 || this.index === i ? 1 : 0.2))
        .attr('y', d => {
          return chartHeight - this.yScale(d.impactPercent) - 64 * Math.sign(d.impactPercent);
        });

      if (!indexMode) {
        const values = this.svg.selectAll('.site-value').data(this.impact);
        values
          .enter()
          .merge(values)
          .transition()
          .duration(700)
          .tween('text', function (d) {
            const text = d3.select(this).text();
            let current = isNaN(text) ? Number(text.split('%')[0].substring(1)) / 100 : text;
            var i = d3.interpolate(Number(current), d.impactPercent);

            return t => {
              d3.select(this).text(d3.format('+.0%')(i(t)));
            };
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.environmental-impact {
  display: flex;
  width: 100%;
  height: 100%;
  padding: 0 0 64px 0;
  box-sizing: border-box;

  .current-site {
    flex-shrink: 0;
    text-align: center;
    margin: auto 16px;

    .title {
      font-weight: bold;
      font-size: 40px;
    }
    .value {
      font-size: 48px;
    }
  }

  .chart {
    flex-grow: 1;
    // background: rgba(red, 0.1);
    width: 100%;
    height: 100%;
    overflow: visible;
  }
}
</style>
